<template>
  <li v-if="!isHidden" data-qa="quote-row" class="row" @click="navigateToDetailsPage">
    <div class="title">
      <strong>{{ quote.Metadata.Name }}</strong
      ><br />
      <span class="metadata">{{ formattedDate }}</span
      ><br />
      <span v-if="komNumber"
        >{{ i18n.t('/Dashboard/Table/Kom Number') }}: {{ komNumber }}<br
      /></span>
      <span data-qa="quote-row-quote-number"
        >{{ i18n.t('/Dashboard/Table/Quote Id') }}: {{ quote.QuoteId }}</span
      >
    </div>
    <div class="customer">
      <span>{{ customerName }}</span
      ><br />
      <span>{{ customerCompanyName }}</span
      ><br />
      <span>{{ leaseCompanyName }}</span>
    </div>
    <div class="salesemployee">
      <span>{{ quote.QuoteDetails.Employee.EmployeeName }}</span>
      <span>{{ quote.QuoteDetails.Dealer.DealerName }}</span>
      <span>{{ quoteEstablishmentId }}</span>
    </div>
    <div :class="getSpecialStatusClass">
      <span data-qa="quote-row-status">{{ quoteStatusDescription }}</span>
      <span v-if="quoteSigningStatusLabel">{{ quoteSigningStatusLabel }}</span>
    </div>
    <div class="actions">
      <app-button
        button-class="icon-stack"
        data-qa="quote-row-button-remark"
        :disabled="!hasRemark"
        @click.stop="toggleRemarkModal(true, $event)"
      >
        <span class="btn-icon">&#x2139;</span>
        <span class="btn-label">info</span>
      </app-button>

      <app-button
        v-if="canViewVcr"
        button-class="icon-stack"
        data-qa="quote-row-button-vcr"
        @click.stop="goToVcr"
      >
        <span class="btn-icon">&#x1F4CA;</span>
        <span class="btn-label">vcr</span>
      </app-button>

      <app-button
        button-class="icon-stack"
        data-qa="quote-row-button-delete"
        @click.stop="toggleDeleteConfirmModal(true, $event)"
      >
        <span class="btn-icon">&#x1F5D1;</span>
        <span class="btn-label">archief</span>
      </app-button>
    </div>

    <modal
      v-if="showDeleteConfirmModal"
      :title="i18n.t('/Common/Confirmation')"
      @close="toggleDeleteConfirmModal(false, $event)"
    >
      <p>{{ i18n.t('/Dashboard/Quotes/ConfirmDeleteText') }}</p>
      <app-button
        data-qa="delete-quote-cancel-button"
        :disabled="isDeleting"
        @click="toggleDeleteConfirmModal(false, $event)"
        >{{ i18n.t('/Common/Cancel') }}</app-button
      >
      <app-button
        :disabled="isDeleting"
        data-qa="delete-quote-confirm-button"
        @click.stop="confirmDelete"
        >{{ i18n.t('/Common/Delete') }}</app-button
      >
    </modal>

    <modal
      v-if="showRemarkModal"
      :title="i18n.t('/Dashboard/RemarkModalTitle')"
      @close="toggleRemarkModal(false, $event)"
    >
      <p>
        {{ quote.QuoteDetails.Remark }}
      </p>
    </modal>
  </li>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import quoteOrderService from '@/services/quoteOrderService.js'
import DateHelper from '@/helpers/dateHelper'
import digitalSigningStatus from '@/helpers/enums/digitalSigningStatus'
import { getStatus } from '@/helpers/statusHelper'
import { mapGetters } from 'vuex'

export default {
  components: {
    Modal,
    AppButton,
  },
  props: {
    quote: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showDeleteConfirmModal: false,
      showRemarkModal: false,
      isHidden: false,
      isDeleting: false,
    }
  },
  computed: {
    ...mapGetters({
      canViewVcr: 'user/canViewVcr',
    }),
    carName() {
      return this.quote.Metadata.Name
    },
    customerName() {
      if (this.quote.QuoteDetails.Customer != null) {
        const initials = this.quote.QuoteDetails.Customer.CustomerInitials || ''
        let fullName = initials
        const middleName = this.quote.QuoteDetails.Customer.CustomerMiddleName || ''
        if (middleName != '') {
          fullName = fullName.concat(' ', middleName)
        }
        const familyName = this.quote.QuoteDetails.Customer.CustomerFamilyName || ''
        return fullName.concat(' ', familyName)
      } else return ''
    },
    customerCompanyName() {
      return this.quote.QuoteDetails.CustomerCompany.CustomerCompanyName || ''
    },
    quoteEstablishmentId() {
      return this.quote.QuoteDetails.Dealer.EstablishmentID
    },
    leaseCompanyName() {
      return this.quote.QuoteDetails.LeaseCompany?.Name || ''
    },
    formattedDate() {
      return DateHelper.formatDate(new Date(this.quote.Metadata.LastUpdatedOn))
    },
    hasRemark() {
      return this.quote.QuoteDetails.Remark != null && this.quote.QuoteDetails.Remark != ''
    },
    komNumber() {
      if (this.quote.QuoteDetails.Cars[0]?.KomNumber?.Number) {
        return this.quote.QuoteDetails.Cars[0]?.KomNumber?.Number
      } else {
        return null
      }
    },
    quoteStatusDescription() {
      return getStatus(this.quote.Status)
    },
    quoteSigningStatusLabel() {
      if (this.quote.Status === 'SALESORDER' && this.quote.SalesOrderSendToAutoline)
        return this.i18n.t(`/dashboard/signingstatus/sentToAutoline`)

      if (this.quote.Status === 'REJECTED')
        return this.i18n.t(`/dashboard/rejected_order/change_order`)

      if (!this.quote.SigningStatus) return null
      switch (this.quote.SigningStatus) {
        case digitalSigningStatus.openedByCustomer:
          return this.i18n.t(`/dashboard/signingstatus/openedByCustomer`)
        case digitalSigningStatus.manualSignCompleted:
          return this.i18n.t(`/dashboard/signingstatus/signManuallyCompleted`)
        case digitalSigningStatus.signCompleted:
          return this.i18n.t(`/dashboard/signingstatus/signCompleted`)
        case digitalSigningStatus.manualSignPending:
          return this.i18n.t(`/dashboard/signingstatus/manualSignPending`)
        case digitalSigningStatus.sentToCustomer:
          return this.i18n.t(`/dashboard/signingstatus/sentToCustomer`)
        case digitalSigningStatus.signedByCustomer:
        case digitalSigningStatus.documentSignedByCustomer:
          return this.i18n.t(`/dashboard/signingstatus/signedByCustomer`)
        default:
          return this.i18n.t(`/dashboard/signingstatus/unknown`)
      }
    },
    getSpecialStatusClass() {
      if (this.quote.Status === 'REJECTED') {
        return 'status orderRejected'
      }
      return 'status'
    },
  },
  methods: {
    navigateToDetailsPage() {
      let query = { ...this.$route.query }

      if (this.$route.params.establishmentId != this.quoteEstablishmentId) {
        query = { returnUrl: this.$route.path, ...query }
      }

      // Add current tab index to preserve tab selection when returning
      query.tab = this.getCurrentOffertesTabIndex()

      this.$router.push({
        name: `configuration-page`,
        params: {
          quoteId: this.quote.QuoteId,
          establishmentId: this.quoteEstablishmentId,
          customerType: this.$route.params.customerType,
        },
        query,
      })
    },
    getCurrentOffertesTabIndex() {
      // Calculate tab index for "Offertes" based on the same logic as QuoteOverviewPage
      const isTest = this.$route.query?.hasOwnProperty('isTest')
      const dealerSettings = this.$store.getters['user/dealerSettings']

      let tabIndex = 0

      // Add Voorraad (New) if in test mode with new stock flow
      if (isTest && dealerSettings.newStockFlowEnabled) {
        tabIndex++
      }

      // Add Voorraad tab
      tabIndex++

      // Offertes is the next tab
      return tabIndex
    },
    toggleRemarkModal(showModal, e) {
      e.stopPropagation()
      if (this.hasRemark) this.showRemarkModal = showModal
    },
    toggleDeleteConfirmModal(showModal, e) {
      e.stopPropagation()
      this.showDeleteConfirmModal = showModal
    },
    async confirmDelete(e) {
      this.isDeleting = true
      await quoteOrderService.deleteQuote(this.quote.QuoteId)
      this.isDeleting = false
      this.isHidden = true
    },
    goToVcr() {
      const currentUrl = window.location.href.replace(window.location.origin, '')

      this.$router.push({
        name: 'vcr-page',
        params: {
          establishmentId: this.$route.params.establishmentId,
          customerTyper: this.$route.params.customerType,
          quoteId: this.quote.QuoteId,
        },
        query: {
          returnUrl: currentUrl,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.metadata {
  color: #a3b8c0;
}

.actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.title,
.customer,
.salesemployee {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.salesemployee,
.status {
  display: flex;
  flex-direction: column;
}

.orderRejected {
  color: orangered;
  font-weight: bold;
}
</style>
